import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Search, User, MessageSquare, ChevronDown, ChevronRight, LogOut, LayoutDashboard, Store, Heart, Bell, Globe, Gift, Crown, Leaf, Briefcase, Calendar, Tag, Shield, HelpCircle, Newspaper, Sparkles, FileText, BookOpen, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';

export default function Navbar() {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileHireOpen, setMobileHireOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);

  const API_BASE = process.env.REACT_APP_BACKEND_URL;
  const API = `${API_BASE}/api`;

  // Fetch unread message count
  useEffect(() => {
    if (!user) return;
    const fetchUnread = async () => {
      try {
        const res = await fetch(`${API}/messages/unread-count`, { headers: getAuthHeaders() });
        if (res.ok) { const d = await res.json(); setUnreadCount(d.count || 0); }
      } catch {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    return () => clearInterval(interval);
  }, [user]);

  // Resolve picture URL (handles relative /api/media/* paths)
  const getPictureUrl = (pic) => {
    if (!pic) return null;
    if (pic.startsWith('http')) return pic;
    if (pic.startsWith('/api/')) return `${API_BASE}${pic}`;
    return pic;
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/browse?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <nav className="en-purple fixed top-0 left-0 right-0 z-50 bg-[#0E0E23]/95 backdrop-blur-xl border-b border-white/10">
      <div className="container-main">
        <div className="flex items-center justify-between h-16 md:h-[72px]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="navbar-logo">
            <span className="font-syne text-xl md:text-2xl font-bold tracking-tight text-holographic">ENTREPRENATIVES</span>
          </Link>

          {/* Desktop Navigation — only THREE inline links. Everything else
             lives in the hamburger drawer so the navbar never overflows. */}
          <div className="hidden md:flex items-center gap-1 ml-6 lg:ml-10">
            <Link
              to="/browse"
              className="text-[#C4B5FD] hover:text-white font-outfit text-sm px-3 py-2 rounded-lg hover:bg-white/10 transition-colors whitespace-nowrap"
              data-testid="nav-hire-vendor"
            >
              Hire a Vendor
            </Link>
            <Link
              to={user?.roles?.includes('vendor') ? '/dashboard/vendor' : '/onboarding/vendor'}
              className="text-[#C4B5FD] hover:text-white font-outfit text-sm px-3 py-2 rounded-lg hover:bg-white/10 transition-colors whitespace-nowrap"
              data-testid="nav-become-vendor"
            >
              Become a Vendor
            </Link>
            <Link
              to="/shop"
              className="text-[#C4B5FD] hover:text-white font-outfit text-sm px-3 py-2 rounded-lg hover:bg-white/10 transition-colors whitespace-nowrap flex items-center gap-1.5"
              data-testid="nav-shop-now"
            >
              <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] text-white">NEW</span>
              Shop Now
            </Link>
            <Link
              to="/create-website"
              className="text-[#C4B5FD] hover:text-white font-outfit text-sm px-3 py-2 rounded-lg hover:bg-white/10 transition-colors whitespace-nowrap flex items-center gap-1.5"
              data-testid="nav-build-website"
            >
              <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-gradient-to-r from-fuchsia-400 to-cyan-300 text-[#0E0E23]">NEW</span>
              Build a Website
            </Link>
          </div>

          {/* Search Bar — HIDDEN. Search lives inside the hamburger drawer
             so the navbar stays minimal and never overflows. */}
          <form onSubmit={handleSearch} className="hidden">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input 
                type="text"
                placeholder="Search vendors..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 h-10 bg-white/10 border-white/10 text-white rounded-full text-sm focus:bg-white/15 focus:border-[#8B5CF6] focus:ring-[#8B5CF6]"
                data-testid="nav-search"
              />
            </div>
          </form>

          {/* Desktop Actions — Log in / Sign up always pinned to the right,
             then the hamburger button. Auto margin pushes the cluster all the
             way to the right since the inline nav and search are hidden. */}
          <div className="hidden md:flex items-center gap-2 ml-auto flex-shrink-0">
            {user ? (
              <>
                <Link to="/messages" data-testid="nav-messages">
                  <Button variant="ghost" size="icon" className="text-[#9CA3AF] hover:text-white hover:bg-white/10 relative">
                    <MessageSquare className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1" data-testid="unread-badge">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </Button>
                </Link>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2 text-[#C4B5FD] hover:text-white hover:bg-white/10" data-testid="user-menu-trigger">
                      {getPictureUrl(user.picture) ? (
                        <img src={getPictureUrl(user.picture)} alt={user.name} className="w-8 h-8 rounded-full object-cover border border-white/10" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-[#8B5CF6]/30 flex items-center justify-center">
                          <span className="text-[#C4B5FD] font-semibold text-sm">{user.name?.[0]}</span>
                        </div>
                      )}
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-white border-white/10">
                    <div className="px-3 py-2 border-b border-white/10">
                      <p className="font-medium text-white">{user.name}</p>
                      <p className="text-sm text-[#9CA3AF]">{user.email}</p>
                    </div>
                    {user.roles?.includes('vendor') ? (
                      // Vendors get the vendor dashboard only (their buyer orders are inside it)
                      <DropdownMenuItem onClick={() => navigate('/dashboard/vendor')} className="cursor-pointer" data-testid="menu-vendor-dashboard">
                        <Store className="w-4 h-4 mr-2" />
                        Vendor Dashboard
                      </DropdownMenuItem>
                    ) : (
                      <>
                        <DropdownMenuItem onClick={() => navigate('/dashboard/consumer')} className="cursor-pointer" data-testid="menu-consumer-dashboard">
                          <LayoutDashboard className="w-4 h-4 mr-2" />
                          My Dashboard
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/onboarding/vendor')} className="cursor-pointer" data-testid="menu-become-vendor">
                          <Store className="w-4 h-4 mr-2" />
                          Become a Vendor
                        </DropdownMenuItem>
                      </>
                    )}
                    {user.roles?.includes('supporter') && (
                      <DropdownMenuItem onClick={() => navigate('/dashboard/supporter')} className="cursor-pointer" data-testid="menu-supporter-dashboard">
                        <Heart className="w-4 h-4 mr-2" />
                        Supporter Dashboard
                      </DropdownMenuItem>
                    )}
                    {user.roles?.includes('builder_client') && (
                      <DropdownMenuItem onClick={() => navigate('/website-builder')} className="cursor-pointer" data-testid="menu-website-builder">
                        <Globe className="w-4 h-4 mr-2" />
                        Website Builder
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => navigate('/invite')} className="cursor-pointer" data-testid="menu-invite-friends">
                      <Gift className="w-4 h-4 mr-2" />
                      Invite Friends
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-[#FCA5A5]" data-testid="menu-logout">
                      <LogOut className="w-4 h-4 mr-2" />
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/auth" data-testid="nav-login">
                  <Button variant="ghost" className="text-[#C4B5FD] hover:text-white font-outfit hover:bg-white/10">
                    Log in
                  </Button>
                </Link>
                <Link to="/auth?mode=register" data-testid="nav-signup">
                  <Button className="bg-yellow-400 hover:bg-yellow-300 text-black font-outfit rounded-full px-6 font-bold">
                    Sign up
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Hamburger menu — visible on all sizes (full-feature drawer) */}
          <button
            className="ml-1 md:ml-2 p-2 text-[#C4B5FD] hover:text-white hover:bg-white/5 rounded-lg transition-colors flex-shrink-0"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Open menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Drawer menu — opens on every breakpoint via the hamburger button */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Solid backdrop scrim — kills the bleed-through from the page
                behind the drawer (especially on iOS Safari where
                `backdrop-filter` is unreliable over high-contrast hero text). */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="fixed inset-0 top-16 bg-[#0E0E23]/90 lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-menu-backdrop"
            />
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18 }}
              className="absolute left-0 right-0 top-full bg-[#13153A] border-t border-white/10 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.8)]"
              data-testid="mobile-menu-panel"
            >
            <div className="container-main py-4 max-h-[calc(100vh-4rem)] overflow-y-auto overscroll-contain lg:max-w-md lg:ml-auto lg:mr-4 lg:rounded-b-2xl lg:border-x lg:border-b lg:border-white/10 lg:bg-[#13153A]/95 lg:shadow-[0_24px_60px_-20px_rgba(0,0,0,0.7)] lg:px-5">
              {/* Mobile Search */}
              <form onSubmit={handleSearch} className="mb-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                  <Input
                    type="text"
                    placeholder="Search vendors..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 h-10 bg-white/10 border-white/10 text-white placeholder:text-[#9CA3AF] rounded-full"
                  />
                </div>
              </form>

              <nav className="space-y-1.5">
                {/* 1. Become a Vendor */}
                <Link
                  to={user?.roles?.includes('vendor') ? '/dashboard/vendor' : '/onboarding/vendor'}
                  className="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-[#9CA3AF] hover:text-white hover:bg-white/5 transition-all"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-become-vendor"
                >
                  <Store size={18} strokeWidth={1.5} />
                  Become a Vendor
                </Link>

                {/* 1b. Shop Now (NEW) */}
                <Link
                  to="/shop"
                  className="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-[#9CA3AF] hover:text-white hover:bg-white/5 transition-all"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-shop-now"
                >
                  <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] text-white">NEW</span>
                  Shop Now
                </Link>

                {/* 2. Hire a Vendor (collapsible) */}
                <div>
                  <button
                    type="button"
                    onClick={() => setMobileHireOpen(!mobileHireOpen)}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-[#9CA3AF] hover:text-white hover:bg-white/5 transition-all"
                    data-testid="mobile-hire-toggle"
                    aria-expanded={mobileHireOpen}
                  >
                    <Briefcase size={18} strokeWidth={1.5} />
                    Hire a Vendor
                    <ChevronRight
                      size={16}
                      className={`ml-auto transition-transform ${mobileHireOpen ? 'rotate-90' : ''}`}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {mobileHireOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden pl-3 mt-1 space-y-1.5"
                      >
                        <Link
                          to="/jobs/new"
                          className="flex items-center justify-between gap-2 px-4 py-3 rounded-2xl bg-gradient-to-r from-fuchsia-500/15 to-purple-500/15 border border-purple-400/40 shadow-[0_0_18px_rgba(168,85,247,0.18)]"
                          onClick={() => setMobileMenuOpen(false)}
                          data-testid="mobile-jobrequest"
                        >
                          <span className="flex items-center gap-2 font-bold text-purple-200 text-sm">
                            <span className="w-1.5 h-1.5 rounded-full bg-fuchsia-400 animate-pulse" />
                            Request a Job
                            <span className="px-1.5 py-0.5 rounded-full bg-purple-600 text-white text-[9px] font-bold tracking-wider">NEW</span>
                          </span>
                          <span className="text-purple-300">→</span>
                        </Link>
                        <Link
                          to="/browse"
                          className="flex items-center gap-3 px-4 py-2.5 rounded-2xl text-sm text-[#9CA3AF] hover:text-white hover:bg-white/5 transition-all"
                          onClick={() => setMobileMenuOpen(false)}
                          data-testid="mobile-browse-vendors"
                        >
                          <span className="w-1 h-1 rounded-full bg-[#A78BFA]" />
                          Browse all vendors
                        </Link>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* 3. Create a Website (NEW — rainbow glow) */}
                <Link
                  to="/create-website"
                  className="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-bold text-white bg-gradient-to-r from-[#8B5CF6]/30 via-[#3B82F6]/25 to-[#06B6D4]/20 border border-[#8B5CF6]/45 shadow-[0_0_22px_rgba(139,92,246,0.28)] relative overflow-hidden"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-create-website"
                >
                  <Sparkles size={18} className="text-[#A78BFA]" />
                  <span className="bg-gradient-to-r from-[#A78BFA] via-[#60A5FA] to-[#22D3EE] bg-clip-text text-transparent">
                    Create a Website
                  </span>
                  <span className="ml-auto text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white">NEW</span>
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-[#A78BFA] animate-pulse shadow-[0_0_10px_#A78BFA] hidden" />
                </Link>

                {/* 4. The Feed */}
                <Link
                  to="/feed"
                  className="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-[#9CA3AF] hover:text-white hover:bg-white/5 transition-all"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-feed"
                >
                  <Newspaper size={18} strokeWidth={1.5} />
                  The Feed
                </Link>

                {/* 5. Events */}
                <Link
                  to="/events"
                  className="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-[#9CA3AF] hover:text-white hover:bg-white/5 transition-all"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-events"
                >
                  <Calendar size={18} strokeWidth={1.5} />
                  Events
                </Link>

                {/* 6. Pricing */}
                <Link
                  to="/pricing"
                  className="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-[#9CA3AF] hover:text-white hover:bg-white/5 transition-all"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-pricing"
                >
                  <Tag size={18} strokeWidth={1.5} />
                  Pricing
                </Link>

                {/* 7. Trust & Safety */}
                <Link
                  to="/trust-safety"
                  className="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-[#9CA3AF] hover:text-white hover:bg-white/5 transition-all"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-trust-safety"
                >
                  <Shield size={18} strokeWidth={1.5} />
                  Trust & Safety
                </Link>

                {/* 8. Founding Vendors (gold glow) */}
                <Link
                  to="/founding-members"
                  className="relative flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-[#FCD34D] bg-gradient-to-r from-[#F59E0B]/15 to-[#D97706]/5 border border-[#F59E0B]/40 shadow-[0_0_22px_rgba(245,158,11,0.22)]"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-founding-vendors"
                >
                  <Crown size={18} strokeWidth={1.8} className="text-[#FCD34D]" />
                  Founding Vendors
                  <span className="ml-auto text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-gradient-to-r from-[#F59E0B] to-[#FCD34D] text-[#0E0E23]">THE FIFTY</span>
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-[#FCD34D] animate-pulse shadow-[0_0_10px_#FCD34D] hidden" />
                </Link>

                {/* 9. Founding Non-Profits (emerald glow) */}
                <Link
                  to="/founding-nonprofits"
                  className="relative flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-[#34D399] bg-gradient-to-r from-[#10B981]/15 to-[#059669]/5 border border-[#10B981]/40 shadow-[0_0_22px_rgba(16,185,129,0.22)]"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-founding-nonprofits"
                >
                  <Leaf size={18} strokeWidth={1.8} className="text-[#34D399]" />
                  Founding Non-Profits
                  <span className="ml-auto text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-gradient-to-r from-[#10B981] to-[#34D399] text-[#0E0E23]">THE TEN</span>
                </Link>

                {/* 10. FAQ */}
                <Link
                  to="/faq"
                  className="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-[#9CA3AF] hover:text-white hover:bg-white/5 transition-all"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-faq"
                >
                  <HelpCircle size={18} strokeWidth={1.5} />
                  FAQ
                </Link>
              </nav>

              {/* Auth section */}
              <div className="mt-4 pt-4 border-t border-white/10">
                {user ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 px-2 mb-2">
                      {getPictureUrl(user.picture) ? (
                        <img src={getPictureUrl(user.picture)} alt={user.name} className="w-10 h-10 rounded-full object-cover border border-white/10" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-[#8B5CF6]/30 flex items-center justify-center">
                          <span className="text-[#C4B5FD] font-semibold">{user.name?.[0]}</span>
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="font-medium text-white text-sm truncate">{user.name}</p>
                        <p className="text-xs text-[#9CA3AF] truncate">{user.email}</p>
                      </div>
                    </div>
                    {user.roles?.includes('vendor') ? (
                      <Link
                        to="/dashboard/vendor"
                        className="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-bold text-white bg-gradient-to-r from-[#8B5CF6]/30 to-[#3B82F6]/20 border border-[#8B5CF6]/40 shadow-[0_0_20px_rgba(139,92,246,0.22)]"
                        onClick={() => setMobileMenuOpen(false)}
                        data-testid="mobile-vendor-dashboard"
                      >
                        <LayoutDashboard size={18} className="text-[#A78BFA]" />
                        Vendor Dashboard
                      </Link>
                    ) : (
                      <Link
                        to="/dashboard/consumer"
                        className="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-[#9CA3AF] hover:text-white hover:bg-white/5 transition-all"
                        onClick={() => setMobileMenuOpen(false)}
                        data-testid="mobile-consumer-dashboard"
                      >
                        <LayoutDashboard size={18} strokeWidth={1.5} />
                        My Dashboard
                      </Link>
                    )}
                    {!user.roles?.includes('vendor') && (
                      <Link
                        to="/my-orders"
                        className="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-[#9CA3AF] hover:text-white hover:bg-white/5 transition-all"
                        onClick={() => setMobileMenuOpen(false)}
                        data-testid="mobile-my-orders"
                      >
                        <Package size={18} strokeWidth={1.5} />
                        My Orders
                      </Link>
                    )}
                    <Link
                      to="/messages"
                      className="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-[#9CA3AF] hover:text-white hover:bg-white/5 transition-all"
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid="mobile-messages"
                    >
                      <MessageSquare size={18} strokeWidth={1.5} />
                      Messages
                      {unreadCount > 0 && (
                        <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      )}
                    </Link>
                    <Link
                      to="/invite"
                      className="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-[#9CA3AF] hover:text-white hover:bg-white/5 transition-all"
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid="mobile-invite-friends"
                    >
                      <Gift size={18} strokeWidth={1.5} />
                      Invite Friends
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-[#FCA5A5] hover:bg-red-500/10 transition-all"
                      data-testid="mobile-logout"
                    >
                      <LogOut size={18} strokeWidth={1.5} />
                      Log out
                    </button>
                  </div>
                ) : (
                  <Link
                    to="/auth"
                    className="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-bold text-white bg-gradient-to-r from-[#8B5CF6]/30 to-[#3B82F6]/20 border border-[#8B5CF6]/40 shadow-[0_0_20px_rgba(139,92,246,0.22)]"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-login"
                  >
                    <User size={18} className="text-[#A78BFA]" />
                    Log in
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
}
